/**
 * Mobile Menu Component
 *
 * Responsive hamburger menu for mobile devices that provides navigation
 * and user account options in a compact dropdown interface.
 *
 * Features:
 * - Hamburger icon that transforms to X when open
 * - Smooth dropdown animation
 * - User information display
 * - Sign out functionality
 * - Click outside to close
 * - Responsive design for mobile/tablet breakpoints
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

interface MobileMenuProps {
  className?: string;
}

export default function MobileMenu({ className = "" }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();

  /**
   * Handle user logout and close menu
   */
  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  /**
   * Close menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Close menu when pressing Escape key
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {/* Hamburger Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex flex-col items-center justify-center w-10 h-10 rounded-lg hover:bg-tp-brand/10 transition-colors"
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
      >
        <div className="flex flex-col items-center justify-center w-6 h-6">
          {/* Top bar */}
          <div
            className={`w-6 h-0.5 bg-tp-line transition-all duration-300 ${
              isOpen ? 'rotate-45 translate-y-1.5' : 'mb-1'
            }`}
          />
          {/* Middle bar */}
          <div
            className={`w-6 h-0.5 bg-tp-line transition-all duration-300 ${
              isOpen ? 'opacity-0' : 'mb-1'
            }`}
          />
          {/* Bottom bar */}
          <div
            className={`w-6 h-0.5 bg-tp-line transition-all duration-300 ${
              isOpen ? '-rotate-45 -translate-y-1.5' : ''
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      <div
        className={`absolute right-0 top-12 w-64 bg-white rounded-lg shadow-xl border border-tp-brand/20 transition-all duration-200 origin-top-right ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
        }`}
      >
        <div className="p-4">
          {/* User Info Section */}
          <div className="flex items-center space-x-3 pb-4 mb-4 border-b border-tp-brand/20">
            <div className="w-10 h-10 bg-gradient-to-r from-tp-brand to-tp-leaf rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-lg">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-tp-brand truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-tp-line truncate">
                {user?.email || 'user@example.com'}
              </p>
            </div>
          </div>

          {/* Menu Options */}
          <div className="space-y-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-3 py-2 text-left text-tp-line hover:text-tp-brand hover:bg-tp-brand/5 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}